// A still of the CRM form's first screen (the contact screen of
// web/src/pages/PublicForm.tsx, as it looks framed on a page), drawn from a
// starter's form fields. Galleries and template previews show it where a page
// has no real form yet, so the Form section looks like the form the template
// will create. Static markup only (no inputs, no effects): safe for the server
// render and for thumbnails. Sizes and colors follow polish/crm-public.css.
import type { StarterFormField } from '../starters/types';
import { formInkOn } from '../model/pages';
import { formFieldDesignStyle, formFieldItemStyle, normalizeFormFieldDesign, type FormFieldDesign, type FormFieldKey } from '../model/form-fields';

/** What a gallery passes PageView as `previewForm` (see starters/page-starters.ts starterPreviewForm). */
export interface PreviewForm {
  /** The contact screen's title (absent: "Where should we reach you?"). */
  title?: string;
  /** The line under the title. */
  description?: string;
  fields: StarterFormField[];
}

const keyOf = (f: StarterFormField) => (typeof f === 'string' ? f : f.key);

function Field({ label, value = '', field, design }: { label: string; value?: string; field?: FormFieldKey; design?: FormFieldDesign }) {
  return (
    <span className={design ? 'pf-field grid min-w-0 gap-1.5 text-[13px] font-medium text-[#4b5765] max-[520px]:![grid-column:1_/_-1]' : 'grid gap-1.5 text-[13px] font-medium text-[#4b5765]'} style={field ? formFieldItemStyle(design, field) : undefined}>
      {label}
      <span className={`block ${design ? 'min-h-[var(--pf-field-height,44px)]' : 'min-h-[44px]'} w-full rounded-[6px] border border-[#dfe4ea] bg-white px-3 py-2 text-[16px] font-normal leading-[26px] text-[#0f1924]`}>{value}</span>
    </span>
  );
}

export default function FormMock({ title, description, fields, accent, expanded = false, fieldDesign }: PreviewForm & {
  /** The form accent (formAccentFor): the button's fill. */
  accent: string;
  /** Match a CRM form expanded to fill its page section. */
  expanded?: boolean;
  fieldDesign?: FormFieldDesign;
}) {
  const keys = fields.map(keyOf);
  const design = normalizeFormFieldDesign(fieldDesign);
  return (
    <div data-form-mock className={`relative w-full bg-white font-sans text-[15px] text-[#0f1924] antialiased${design ? ' pf-field-design' : ''}`} style={formFieldDesignStyle(design)}>
      <div aria-hidden className={design?.padding != null ? 'absolute inset-x-[var(--pf-field-padding,24px)] top-5 h-[3px] rounded-full bg-[#dfe4ea] max-[520px]:inset-x-[min(var(--pf-field-padding,16px),20px)] max-[520px]:top-[18px]' : 'absolute inset-x-0 top-0 h-[3px] bg-[#dfe4ea]'} />
      <div className={design?.padding != null ? 'flex justify-center px-[var(--pf-field-padding,24px)] pb-[var(--pf-field-padding,24px)] pt-[calc(var(--pf-field-padding,24px)+24px)] max-[520px]:px-[min(var(--pf-field-padding,16px),20px)] max-[520px]:pb-[min(var(--pf-field-padding,16px),20px)] max-[520px]:pt-11' : 'flex justify-center px-4 pb-[72px] pt-8'}>
        <div className={expanded ? 'w-full' : 'w-full max-w-[640px]'}>
          <p className="m-0 text-[24px] font-semibold leading-[1.3] tracking-[-0.01em] max-[520px]:text-[20px]">{title?.trim() || 'Where should we reach you?'}</p>
          {description?.trim() && <p className="m-0 mt-2.5 text-[15px] leading-normal text-[#4b5765]">{description}</p>}
          <div className={`mt-6 grid ${design ? 'gap-y-[var(--pf-field-gap,14px)] gap-x-[var(--pf-field-gap,12px)] grid-cols-12' : 'gap-[14px]'}${expanded ? '' : ' max-w-[480px]'}`}>
            {design ? <>
              <Field label="First name" field="first_name" design={design} />
              <Field label="Last name" field="last_name" design={design} />
            </> : <div className="grid grid-cols-2 gap-3 max-[520px]:grid-cols-1">
              <Field label="First name" />
              <Field label="Last name" />
            </div>}
            <Field label="Work email" field="email" design={design} />
            {keys.includes('phone') && (
              <span className={design ? 'pf-field grid min-w-0 gap-1.5 text-[13px] font-medium text-[#4b5765] max-[520px]:![grid-column:1_/_-1]' : 'grid gap-1.5 text-[13px] font-medium text-[#4b5765]'} style={formFieldItemStyle(design, 'phone')}>
                Phone
                <span className={design ? 'grid grid-cols-[minmax(72px,104px)_minmax(0,1fr)] gap-2' : 'grid grid-cols-[104px_1fr] gap-2'}>
                  <span className={`block ${design ? 'min-h-[var(--pf-field-height,44px)]' : 'min-h-[44px]'} rounded-[6px] border border-[#dfe4ea] bg-white px-3 py-2 text-[16px] font-normal leading-[26px] text-[#0f1924]`}>US +1</span>
                  <span className={`block ${design ? 'min-h-[var(--pf-field-height,44px)]' : 'min-h-[44px]'} rounded-[6px] border border-[#dfe4ea] bg-white`} />
                </span>
              </span>
            )}
            {keys.includes('company') && <Field label="Company" field="company" design={design} />}
            {design && keys.includes('title') && <Field label="Job title" field="title" design={design} />}
            <span className="mt-1.5 inline-flex min-h-[44px] items-center justify-center justify-self-start rounded-[6px] px-5 text-[15px] font-semibold"
              style={{ background: accent, color: formInkOn(accent), ...(design ? { gridColumn: '1 / -1', width: 'var(--pf-button-width, auto)', justifySelf: 'var(--pf-button-align, start)' } : {}) }}>Submit</span>
          </div>
        </div>
      </div>
    </div>
  );
}
